import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Paper, List, ListItemAvatar,
  Avatar, Chip, Fab, Tabs, Tab, useTheme,
  Card, CardActionArea
} from '@mui/material';
import { Add, Phone, Email, ChevronRight, Groups, Timer, EventNote } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import api from '../../api/axios';
import LoadingState from '../shared/LoadingState';
import EmptyState from '../shared/EmptyState';

import TimesheetManager from './TimesheetManager';
import LeaveApprovalManager from './LeaveApprovalManager';

const TeamScreen = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const [tab, setTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [team, setTeam] = useState([]);

  useEffect(() => {
    if (tab === 0) {
      loadTeam();
    }
  }, [tab]);

  const loadTeam = async () => {
    setLoading(true);
    try {
      const response = await api.get('/admin/employees').catch(() => ({ data: [] }));
      setTeam(response.data || []);
    } catch (err) {
      console.error('Failed to load team:', err);
    }
    setLoading(false);
  };

  const getRoleColor = (role) => {
    switch (role) {
      case 'admin': return 'error';
      case 'manager': return 'primary';
      default: return 'default';
    }
  };

  const tabs = [
    { label: 'Members', icon: <Groups /> },
    { label: 'Timesheets', icon: <Timer /> },
    { label: 'Leave', icon: <EventNote /> }
  ];

  const renderContent = () => {
    if (tab === 1) return <TimesheetManager />;
    if (tab === 2) return <LeaveApprovalManager />;

    // Members Tab (tab 0)
    return (
      <Box>
        {loading ? (
          <LoadingState message="Loading team..." />
        ) : team.length === 0 ? (
          <Paper 
            elevation={0}
            sx={{ 
                borderRadius: 3,
                bgcolor: '#F8FAFC',
                border: '1px dashed #E2E8F0',
                overflow: 'hidden'
            }}
          >
            <EmptyState 
              icon={<Groups />}
              title="No team members found"
              description="Build your team to get started."
              actionLabel="Add Team Member"
              onAction={() => navigate('/team/new')}
            />
          </Paper>
        ) : (
          <List disablePadding>
            {team.map((member) => (
              <Card 
                key={member.id} 
                elevation={0}
                sx={{ 
                  mb: 2, 
                  border: '1px solid #E2E8F0',
                  borderRadius: 3,
                  transition: 'transform 0.1s, box-shadow 0.1s',
                  '&:hover': {
                    transform: 'translateY(-1px)',
                    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05)',
                  }
                }}
              >
                <CardActionArea 
                  onClick={() => navigate(`/team/${member.id}`)}
                  sx={{ p: 2.5 }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                    <ListItemAvatar sx={{ minWidth: 64 }}>
                      <Avatar 
                        sx={{ 
                          bgcolor: member.role === 'admin' ? 'error.main' : 
                                   member.role === 'manager' ? 'primary.main' : 
                                   'secondary.main',
                          color: 'white',
                          width: 56,
                          height: 56,
                          fontSize: '1.25rem',
                          fontWeight: 600,
                          boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                        }}
                      >
                        {member.name?.[0]?.toUpperCase() || 'U'}
                      </Avatar>
                    </ListItemAvatar>
                    <Box sx={{ flexGrow: 1, ml: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                        <Typography variant="subtitle1" fontWeight={700} sx={{ fontSize: '1.05rem' }}>
                          {member.name}
                        </Typography>
                        <Chip
                          label={member.role || 'worker'}
                          size="small"
                          color={getRoleColor(member.role)}
                          variant={member.role === 'worker' ? 'outlined' : 'filled'}
                          sx={{ height: 24, fontWeight: 600, textTransform: 'capitalize' }}
                        />
                      </Box>
                      
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mt: 1 }}>
                        {member.email && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'text.secondary' }}>
                            <Email fontSize="small" sx={{ fontSize: 16 }} />
                            <Typography variant="body2">{member.email}</Typography>
                          </Box>
                        )}
                        {member.phone && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'text.secondary' }}>
                            <Phone fontSize="small" sx={{ fontSize: 16 }} />
                            <Typography variant="body2">{member.phone}</Typography>
                          </Box>
                        )}
                      </Box>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', height: '100%', ml: 1, alignSelf: 'center' }}>
                        <ChevronRight color="action" />
                    </Box>
                  </Box>
                </CardActionArea>
              </Card>
            ))}
          </List>
        )}
        
        <Fab
          color="primary"
          sx={{ 
              position: 'fixed', 
              bottom: 80, 
              right: 24,
              boxShadow: '0 10px 15px -3px rgba(37, 99, 235, 0.4)'
          }}
          onClick={() => navigate('/team/new')}
        >
          <Add />
        </Fab>
      </Box>
    );
  };

  return (
    <Box sx={{ pb: 10 }}>
      <Paper 
        elevation={0} 
        sx={{ 
          mb: 3, 
          borderBottom: '1px solid #E2E8F0', 
          borderRadius: 0, 
          bgcolor: 'transparent' 
        }}
      >
        <Tabs 
          value={tab} 
          onChange={(e, v) => setTab(v)} 
          variant="fullWidth"
          sx={{ 
            '& .MuiTab-root': { 
                fontWeight: 600, 
                textTransform: 'none',
                minHeight: 48,
                fontSize: '0.95rem'
            }
          }}
        >
          {tabs.map((t, idx) => (
            <Tab key={idx} label={t.label} icon={t.icon} iconPosition="start" />
          ))}
        </Tabs>
      </Paper>

      {renderContent()}
    </Box>
  );
};

export default TeamScreen;
